import React, { useState, useMemo } from 'react';
import { useSettings } from '../hooks/useSettings';
import { SearchableSelect } from './SearchableSelect';
import { SettingItem } from '../types';
import { Spinner } from './Spinner';

export const TouchDragTest: React.FC = () => {
    const { categories, tags, isLoading } = useSettings();

    const [includeCategories, setIncludeCategories] = useState<SettingItem[]>([]);
    const [excludeCategories, setExcludeCategories] = useState<SettingItem[]>([]);
    const [includeTags, setIncludeTags] = useState<SettingItem[]>([]);
    const [excludeTags, setExcludeTags] = useState<SettingItem[]>([]);

    const [generatedOutput, setGeneratedOutput] = useState<string | null>(null);

    const handleGenerate = () => {
        const filterObject = {
            categories: {
                include: includeCategories.map(c => c.name),
                exclude: excludeCategories.map(c => c.name),
            },
            tags: {
                include: includeTags.map(t => t.name),
                exclude: excludeTags.map(t => t.name),
            }
        };

        let whereClauses: string[] = [];

        if (includeCategories.length > 0) {
            whereClauses.push(`  c.id IN (${includeCategories.map(c => c.id).join(', ')})`);
        }
        if (excludeCategories.length > 0) {
            whereClauses.push(`  c.id NOT IN (${excludeCategories.map(c => c.id).join(', ')})`);
        }
        if (includeTags.length > 0) {
            // Simulating JSON_CONTAINS logic for tags
            const tagConditions = includeTags.map(t => `JSON_CONTAINS(r.tagIds, '${t.id}')`);
            whereClauses.push(`  (${tagConditions.join(' OR ')})`);
        }
        if (excludeTags.length > 0) {
            const tagConditions = excludeTags.map(t => `NOT JSON_CONTAINS(r.tagIds, '${t.id}')`);
            whereClauses.push(`  (${tagConditions.join(' AND ')})`);
        }

        const sqlQuery = `SELECT * \nFROM receipts r\nLEFT JOIN categories c ON r.categoryId = c.id\n` + 
                       (whereClauses.length > 0 ? `WHERE\n${whereClauses.join(' AND\n')};` : ';');

        const output = `--- OBJEKT PRO API ---\n${JSON.stringify(filterObject, null, 2)}\n\n--- PŘÍKLAD SQL DOTAZU ---\n${sqlQuery}`;
        setGeneratedOutput(output);
    };

    const clearFilters = () => {
        setIncludeCategories([]);
        setExcludeCategories([]);
        setIncludeTags([]);
        setExcludeTags([]);
        setGeneratedOutput(null);
    };
    
    const hasActiveFilters = useMemo(() => {
        return includeCategories.length > 0 || excludeCategories.length > 0 || includeTags.length > 0 || excludeTags.length > 0;
    }, [includeCategories, excludeCategories, includeTags, excludeTags]);

    if (isLoading) {
        return <Spinner text="Načítám nastavení..." />;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow space-y-6">
            <div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Prototyp filtrování s negací</h3>
                <p className="text-sm text-gray-600">
                    Vyzkoušejte, jak by fungovalo filtrování s možností vyloučit určité položky. 
                    Po kliknutí na tlačítko se níže zobrazí datová struktura, která by se posílala na server, a příklad, jak by server mohl sestavit SQL dotaz.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t pt-6">
                {/* Kategorie */}
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Filtrování podle kategorií</h4>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Zahrnout kategorie</label>
                        <SearchableSelect
                            isMulti
                            options={categories}
                            value={includeCategories}
                            onChange={(v) => setIncludeCategories(v as SettingItem[])}
                            placeholder="Vyberte kategorie..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vyloučit kategorie</label>
                        <SearchableSelect
                            isMulti
                            options={categories}
                            value={excludeCategories}
                            onChange={(v) => setExcludeCategories(v as SettingItem[])}
                            placeholder="Vyberte kategorie k vyloučení..."
                        />
                    </div>
                </div>

                {/* Štítky */}
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Filtrování podle štítků</h4>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Zahrnout štítky</label>
                        <SearchableSelect
                            isMulti
                            options={tags}
                            value={includeTags}
                            onChange={(v) => setIncludeTags(v as SettingItem[])}
                            placeholder="Vyberte štítky..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vyloučit štítky</label>
                        <SearchableSelect
                            isMulti
                            options={tags}
                            value={excludeTags}
                            onChange={(v) => setExcludeTags(v as SettingItem[])}
                            placeholder="Vyberte štítky k vyloučení..."
                        />
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-4 border-t pt-6">
                <button
                    onClick={handleGenerate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                    disabled={!hasActiveFilters}
                >
                    Generovat dotaz
                </button>
                 {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-sm text-gray-600 hover:underline">
                        Vymazat filtry
                    </button>
                )}
            </div>

            {generatedOutput && (
                <div className="mt-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Výstup simulace</h4>
                    <pre className="bg-gray-100 p-4 rounded text-sm text-left whitespace-pre-wrap break-all font-mono">
                        <code>
                            {generatedOutput}
                        </code>
                    </pre>
                </div>
            )}
        </div>
    );
};
